export function getDatasourceId(
  def?: Pick<datasources.ICreateDataSource, 'id' | 'namespace'>,
) {
  if (!def) return '';

  const { id, namespace } = def;

  return namespace && id ? `${namespace}:${id}` : id;
}

/**
 * checks if the attribute is a simple attribute.
 *
 * can be a scalar attribute or calculated attribute and have basic data types
 *
 * like string, number, boolean, date, time, datetime, etc.
 * @param attribute the attribute to check
 * @returns whether the attribute is a scalar or not
 */
export function isSimpleAttribute(attribute: datasources.IAttribute) {
  return [
    'bool',
    'word',
    'string',
    'text',
    'uuid',
    'short',
    'long',
    'number',
    'long64',
    'duration',
    'object',
    'date',
    'image',
    'blob',
  ].includes(attribute.type || '');
}

/**
 *
 * @param attribute the attribute to check
 * @returns whether the attribute is an object or not
 */
export function isObjectAttribute(attribute: datasources.IAttribute) {
  return attribute.type === 'object';
}

/**
 * A related Entities attribute can be
 * - an attribute with kind 'relatedEntities'
 * - calculated with type suffixed with 'Selection'
 * @param attribute the attribute to check
 * @returns weather the attribute is a Related Entities.
 */
export function isRelatedEntitiesAttribute(attribute: datasources.IAttribute) {
  return (
    attribute.kind === 'relatedEntities' ||
    attribute.type?.includes('Selection') ||
    attribute.behavior === 'relatedEntities'
  );
}

/**
 * A related Entities attribute can be
 * - an attribute with kind 'relatedEntity'
 * - calculated with type which is not suffixed with 'Selection' and not a basic data type
 * @param attribute the attribute to check
 * @returns weather the attribute is a Related Entities.
 */
export function isRelatedEntityAttribute(attribute: datasources.IAttribute) {
  return (
    attribute.kind === 'relatedEntity' ||
    attribute.behavior === 'relatedEntity' ||
    !isSimpleAttribute(attribute)
  );
}

/**
 * A related Entities attribute can be
 * - a related entity attribute
 * - a related entities attribute
 * @param attribute the attribute to check
 * @returns weather the attribute is a Related Entities.
 */
export function isRelatedAttribute(attribute: datasources.IAttribute) {
  return (
    isRelatedEntitiesAttribute(attribute) || isRelatedEntityAttribute(attribute)
  );
}

export function isDataStoreMethod(method: catalog.IMethod): boolean {
  return method.applyTo === 'dataStore';
}

export function isDataClassMethod(method: catalog.IMethod): boolean {
  return method.applyTo === 'dataClass';
}

export function isEntityMethod(method: catalog.IMethod): boolean {
  return method.applyTo === 'entity';
}

export function isEntitySelectionMethod(method: catalog.IMethod): boolean {
  return method.applyTo === 'entityCollection';
}

export function isEntityDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is datasources.ICreateDataSourceEntity {
  return datasource.type === 'entity';
}
export function isEntitySelectionDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is datasources.ICreateDataSourceEntitySelection {
  return datasource.type === 'entitysel';
}
export function isArrayDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is datasources.ICreateDataSourceArray {
  return (
    (datasource as datasources.ICreateDataSourceArray).dataType === 'array'
  );
}
export function isObjectDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is datasources.ICreateDataSourceObject {
  return (
    (datasource as datasources.ICreateDataSourceObject).dataType === 'object'
  );
}
export function isScalarDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is datasources.ICreateDataSourceScalar {
  return datasource.type === 'scalar';
}
export function isSimpleDatasource(
  datasource: datasources.ICreateDataSource,
): datasource is
  | datasources.ICreateDataSourceArray
  | datasources.ICreateDataSourceObject
  | datasources.ICreateDataSourceScalar {
  return (
    isArrayDatasource(datasource) ||
    isObjectDatasource(datasource) ||
    isScalarDatasource(datasource)
  );
}

export const isEntity = (datasource: datasources.DataSource) => {
  return (
    datasource !== undefined &&
    datasource !== null &&
    datasource.type === 'entity'
  );
};
export const isEntitySelection = (datasource: datasources.DataSource) => {
  return datasource.type === 'entitysel';
};
export const _isArrayDatasource = (datasource: datasources.DataSource) => {
  return (
    datasource.type === 'array' ||
    (datasource.type === 'scalar' && datasource.dataType === 'array')
  );
};
export const _isObjectDatasource = (datasource: datasources.DataSource) => {
  return (
    datasource.type === 'object' ||
    (datasource.type === 'scalar' && datasource.dataType === 'object')
  );
};
export const isDateDatasource = (datasource: datasources.DataSource) => {
  return datasource.type === 'scalar' && datasource.dataType === 'date';
};

export function loadDS(): Promise<void> {
  return new Promise((resolve, reject) => {
    (window as any).addEventListener('bundle:ready', (ev: any) => {
      return ev.data ? resolve() : reject(ev.caughtErr);
    });
  });
}

export function loadI18n(): Promise<void> {
  return new Promise((resolve) => {
    (window as any).addEventListener('i18n:ready', () => {
      return resolve();
    });
  });
}
